
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/contexts/AuthContext';
import { ConnectApiFormData } from '@/components/accounts/connect-api-dialog/types';
import { connectApiFormSchema } from '@/components/accounts/connect-api-dialog/schema';
import { useSimpleCTraderOAuth } from './use-simple-ctrader-oauth';
import { apiConnectionService } from '@/services/api-connection-service';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useCTraderInvalidation } from './use-ctrader-invalidation';
import { useState, useCallback } from 'react';
import { memoryOAuthCache } from '@/utils/oauth-cache-memory';

interface UseSimplifiedConnectApiFormProps {
  cspAccountId: string;
  onSuccess?: () => void;
}

export function useSimplifiedConnectApiForm({
  cspAccountId,
  onSuccess
}: UseSimplifiedConnectApiFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { invalidateProfileQueries, invalidateResourceUsage } = useCTraderInvalidation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<ConnectApiFormData>({
    resolver: zodResolver(connectApiFormSchema),
    defaultValues: {
      csp_account_id: cspAccountId,
      api_name: 'Trade Bot 365 Official Application',
      client_id: 'admin-managed',
      secret_id: 'admin-managed',
      trading_account_id: '',
      access_token: '',
      refresh_token: '',
      expires_in: undefined,
    },
  });

  const {
    isAuthenticating,
    isLoading,
    tokens,
    tradingAccounts,
    rawAccounts,
    currentError,
    canRetry,
    startOAuth,
    retryAuthentication,
    resetState
  } = useSimpleCTraderOAuth();

  // Check if authenticated (has tokens) - separate from account loading
  const authenticated = !!tokens && !!tokens.accessToken;

  const selectedAccount = tradingAccounts.find(
    account => account.id === form.watch('trading_account_id')
  );

  const canConnect = authenticated && !!selectedAccount;

  const handleAuthenticate = async () => {
    await startOAuth(cspAccountId);
  };

  const handleRetry = async () => {
    await retryAuthentication(cspAccountId);
  };

  // Enhanced cleanup function with better UX feedback
  const cleanupTokensAndCache = async () => {
    // Show loading state during cleanup
    const cleanupToast = toast({
      title: "Cleaning up...",
      description: "Finalizing your connection",
    });
    
    // Clear all cTrader related localStorage
    const keysToRemove = [
      'ctrader_oauth_result',
      'ctrader_form_data', 
      'ctrader_connection_id',
      'ctrader_return_path'
    ];
    
    keysToRemove.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear memory cache (which also clears localStorage fallback)
    memoryOAuthCache.clearAll();
    
    // Reset OAuth state
    resetState();
    
    // Invalidate all relevant queries to refresh data
    await Promise.all([
      invalidateProfileQueries(cspAccountId, 'all'),
      invalidateResourceUsage(),
      queryClient.invalidateQueries({ queryKey: ['ctraderAccounts'] }),
      queryClient.invalidateQueries({ queryKey: ['apiConnections'] }),
      queryClient.invalidateQueries({ queryKey: ['tradingAccounts'] })
    ]);
  };

  const handleSubmit = async (data: ConnectApiFormData) => {
    if (!tokens || !selectedAccount || !user?.id) {
      toast({
        title: "Error",
        description: "Missing required data for connection",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Show progress toast for better UX
      const progressToast = toast({
        title: "Connecting Account",
        description: "Setting up your trading account connection...",
      });

      // Find the raw account data to get the original balance in cents
      const rawAccount = rawAccounts.find(acc => acc.accountId === selectedAccount.id);
      
      // Create selectedAccount with raw balance (in cents) for database trigger to convert
      const accountForSubmission = {
        ...selectedAccount,
        // Use raw balance from rawAccounts if available, otherwise multiply by 100 to reverse the conversion
        balance: rawAccount ? rawAccount.balance : selectedAccount.balance * 100
      };

      // Create atomic connection with all data at once
      await apiConnectionService.createAtomicConnection({
        cspAccountId,
        apiName: 'Trade Bot 365 Official Application',
        clientId: 'admin-managed',
        secretId: 'admin-managed',
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiresIn: tokens.expiresIn,
        selectedAccount: accountForSubmission
      });

      toast({
        title: "Success! 🎉",
        description: `${selectedAccount.name} successfully connected!`,
      });

      // Close dialog first for better UX
      onSuccess?.();

      // Enhanced cleanup with delay for better perceived performance
      setTimeout(async () => {
        await cleanupTokensAndCache();
      }, 500);

    } catch (error) {
      console.error('Error creating connection:', error);
      
      // Enhanced error handling with more specific messages
      let errorMessage = "Failed to connect trading account";
      
      if (error instanceof Error) {
        if (error.message.includes('network')) {
          errorMessage = "Network error - please check your connection";
        } else if (error.message.includes('timeout')) {
          errorMessage = "Connection timeout - please try again";
        } else if (error.message.includes('unauthorized')) {
          errorMessage = "Authentication failed - please reconnect";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Connection Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = useCallback(() => {
    resetState();
    
    form.reset({
      csp_account_id: cspAccountId,
      api_name: 'Trade Bot 365 Official Application',
      client_id: 'admin-managed',
      secret_id: 'admin-managed',
      trading_account_id: '',
      access_token: '',
      refresh_token: '',
      expires_in: undefined,
    });
  }, [cspAccountId, form, resetState]);

  return {
    form,
    isLoading: isLoading || isSubmitting,
    isAuthenticating,
    authenticated,
    tradingAccounts,
    selectedAccount,
    canConnect,
    currentError,
    canRetry,
    handleAuthenticate,
    handleRetry,
    handleSubmit,
    resetForm
  };
}
